// FICHE DE RELECTURE d'un brouillon (SA-1, maquette `showDraftCard`) : relecture CHAMP PAR CHAMP de
// l'identité DÉPOSÉE, écart Sirene calculé côté serveur avec « Prendre cette valeur » (la valeur alignée
// part dans le wizard à la validation), panneau Contrôles (dérivés des champs réels), panneau « Attribué
// par nous » (ce que le formulaire ne porte JAMAIS : GLN, numérotation, radical, licences).
// Actions RÉELLES : « Demander un complément » = motif OBLIGATOIRE → statut `to_correct` + lien de
// complément envoyé au déposant (rouvre SON dossier pré-rempli) — l'issue de l'e-mail est restituée
// telle quelle ; « Valider » = ouvre le WIZARD prérempli, le statut passe `validated` à la création
// effective. §23.0 : uniquement les champs déposés, rien d'inventé.
import { dateTimeLong } from '@orgazone/date-format';
import { alert, showToast, useOverlayPanel } from '@orgazone/ui-web';
import { useId, useMemo, useState } from 'react';
import { api, type DraftRow } from '../api';
import { EmptyState } from '@orgazone/ui-web';
import { ProvisioningWizard, type Prefill } from './ProvisioningWizard';

import { Pill } from '../ui';
const STATUS: Record<DraftRow['status'], { label: string; bg: string; fg: string }> = {
  deposited: { label: 'à relire', bg: 'var(--wa-bg)', fg: 'var(--wa-fg)' },
  to_correct: { label: 'complément demandé', bg: 'var(--vi-bg)', fg: 'var(--vi-fg)' },
  validated: { label: 'validé', bg: 'var(--ok-bg)', fg: 'var(--ok-fg)' },
};

// Libellés FR des clés d'identité connues (clé inconnue → affichée telle quelle, jamais masquée).
const FIELD_LABELS: Record<string, string> = {
  name: 'Raison sociale', commercialName: 'Nom commercial', siren: 'SIREN', siret: 'SIRET',
  siretHq: 'SIRET du siège',
  naf: 'Code NAF', legalForm: 'Forme juridique', vatIntra: 'TVA intracom.',
  street: 'Adresse', voie: 'Adresse', cp: 'Code postal', ville: 'Ville', city: 'Ville',
  email: 'E-mail', tel: 'Téléphone', prenom: 'Prénom', nom: 'Nom',
  contactNom: 'Contact', businessKind: 'Métier', kind: 'Type d’établissement',
  // ⚠️ LE CALENDRIER D'EXPLOITATION — sans libellé, la ligne s'affiche « magasin · timezone », que
  //    personne ne relit comme une question à vérifier.
  timezone: 'Fuseau horaire', openDays: 'Jours d’ouverture',
  // ── Réglages DÉPOSÉS par le formulaire (§16.2.8octies) ──────────────────────────────────────
  header: 'En-tête de ticket', footer: 'Pied de ticket',
  enabled: 'Fidélité active', mode: 'Mode de restitution', useFrom: 'Utilisable à partir de',
  conversion: 'Taux de conversion', welcomeBonus: 'Bonus de bienvenue', tiersOn: 'Paliers actifs',
  windowDays: 'Fenêtre de calcul (j)', keepTier: 'Protection du statut', protectDays: 'Durée de protection (j)',
  allowCumul: 'Cumul autorisé', printVoucher: 'Impression du bon', forbidImmediate: 'Usage immédiat interdit',
};

/** Sections du dossier — un chemin racine devient un titre lisible plutôt qu'un préfixe technique. */
const SECTION_LABELS: Record<string, string> = {
  address: 'Siège', contact: 'Contact', store: 'Magasin',
  ticket: 'Ticket', validity: 'Validités', fid: 'Fidélité', settings: 'Réglages',
};

const isPlain = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Les jours, en toutes lettres — un relecteur ne lit pas « 1, 3, 6 ». */
const JOURS_FR = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'] as const;

/**
 * Rendu HUMAIN d'une valeur terminale — un booléen déposé se lit « Oui », pas « true ».
 *
 * ⚠️ UNE LISTE DE PRIMITIVES SE LIT, elle ne se COMPTE pas. « 7 élément(s) » est une réponse utile
 * pour un tableau d’objets, et une réponse vide pour des jours d’ouverture : le relecteur valide un
 * dossier sans savoir si le magasin ferme le lundi — c’est-à-dire sans pouvoir corriger ce que le
 * repli du back-office rendra silencieusement faux.
 */
function afficher(v: unknown, cle?: string): string {
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  if (Array.isArray(v)) {
    if (cle === 'openDays') return v.map((j) => JOURS_FR[Number(j) - 1] ?? String(j)).join(', ');
    return v.every((x) => x === null || typeof x !== 'object')
      ? v.map(String).join(', ')
      : `${v.length} élément(s)`;
  }
  return String(v);
}

/**
 * Aplatit l'identité déposée en lignes lisibles.
 *
 * ⚠️ RÉCURSIF, et ce n'est pas un raffinement : l'ancienne version ne descendait QU'UN niveau. Or le
 * formulaire de dépôt envoie `store.address` et `settings.{ticket,validity,fid}` — donc des objets au
 * niveau 2, que `String()` rendait « [object Object] ». L'équipe validait un dossier en croyant que
 * ces réglages n'avaient pas été remplis (§23.0 : l'écran ne ment jamais).
 *
 * ⚠️ Le CHEMIN sert de repli au libellé : une clé future non traduite s'affiche « magasin.zzz »,
 * lisible, plutôt que de disparaître ou de se confondre avec une clé homonyme d'une autre section.
 */
function flattenIdentity(identity: unknown, prefixe = ''): { key: string; label: string; value: string }[] {
  if (!isPlain(identity)) return [];
  const out: { key: string; label: string; value: string }[] = [];
  for (const [k, v] of Object.entries(identity)) {
    if (v == null || v === '') continue;
    const chemin = prefixe ? `${prefixe}.${k}` : k;
    if (isPlain(v)) {
      out.push(...flattenIdentity(v, SECTION_LABELS[k] ?? k));
    } else {
      out.push({ key: chemin, label: FIELD_LABELS[k] ?? (prefixe ? `${prefixe} · ${k}` : k), value: afficher(v, k) });
    }
  }
  return out;
}

/**
 * LE CALENDRIER DÉPOSÉ, lu à la SOURCE plutôt que dans les lignes aplaties.
 *
 * ⚠️ 🔴 POURQUOI PAS `get('timezone')`. L’aplatissement est fait pour l’AFFICHAGE : il traduit les
 * valeurs en texte (les jours deviennent « lundi, mardi »). Y repêcher une donnée pour la réinjecter
 * dans un formulaire ferait voyager un LIBELLÉ là où le wizard attend des nombres — un défaut muet,
 * qui ne se verrait qu’au moment où le magasin n’envoie pas le bon jour.
 *
 * ⚠️ RIEN N’EST INVENTÉ : un dossier sans calendrier rend un objet VIDE, et le wizard garde alors ses
 * propres défauts. Poser ici un fuseau que le client n’a pas choisi le rendrait indiscernable d’une
 * réponse réelle.
 */
function calendrierDepose(identity: unknown): { timezone?: string; openDays?: number[] } {
  if (!isPlain(identity)) return {};
  const st = identity.store;
  if (!isPlain(st)) return {};
  const jours = Array.isArray(st.openDays)
    ? st.openDays.map(Number).filter((n) => Number.isInteger(n) && n >= 1 && n <= 7)
    : [];
  return {
    ...(typeof st.timezone === 'string' && st.timezone.trim() ? { timezone: st.timezone.trim() } : {}),
    ...(jours.length ? { openDays: [...new Set(jours)].sort((a, b) => a - b) } : {}),
  };
}

export function DraftDetail({ draft, onBack, onChanged }: { draft: DraftRow; onBack: () => void; onChanged: () => void }) {
  const st = STATUS[draft.status];
  const fields = useMemo(() => flattenIdentity(draft.identity), [draft.identity]);
  // Écart Sirene SERVEUR (clé → valeur Sirene). « Prendre cette valeur » = surcharge LOCALE, injectée au
  // wizard à la validation (le brouillon déposé reste intact — c'est la saisie du client, pas la nôtre).
  const gap = useMemo(() => (isPlain(draft.sireneGap) ? draft.sireneGap : null), [draft.sireneGap]);
  const [taken, setTaken] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const effective = (key: string, deposited: string) => taken[key] ?? deposited;

// Motif du complément : saisi AVANT l'envoi — il part dans l'e-mail et en bannière du formulaire
  // rouvert ; sans lui, le déposant ne saurait pas quoi corriger (le serveur le refuse d'ailleurs).
  const [askingReason, setAskingReason] = useState(false);
  const [reason, setReason] = useState('');
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Refusé pendant l'envoi, comme le clic sur le fond. Le focus va au motif par ce socle, jamais par
  // `autoFocus` : posé avant lui, il serait retenu comme déclencheur et le focus ne reviendrait nulle part.
  const { panelRef: reasonPanel } = useOverlayPanel({ open: askingReason, onClose: () => setAskingReason(false), blockClose: () => busy });
  const reasonTitle = useId();
  const requestCorrection = async () => {
    setBusy(true);
    try {
      const r = await api.requestDraftChanges(draft.id, reason.trim());
      setAskingReason(false); setReason('');
      // L'issue RÉELLE : e-mail parti, ou lien à transmettre soi-même — jamais un succès présumé.
      void alert({
        title: 'Complément demandé',
        message: r.mail.ok
          ? `Lien de complément envoyé au déposant — il rouvre son dossier pré-rempli. Expire le ${new Date(r.expiresAt).toLocaleDateString('fr-FR')}.`
          : `Lien émis, mais l'e-mail n'est PAS parti (${r.mail.error ?? 'raison inconnue'}). Transmettez-le vous-même : ${r.url}`,
        tone: 'info',
      });
      onChanged();
    } catch (e) { void alert({ title: 'Complément impossible', message: (e as Error).message }); }
    finally { setBusy(false); }
  };

  // Préremplissage du wizard depuis l'identité (valeurs alignées Sirene comprises).
  const prefill: Prefill = useMemo(() => {
    const get = (k: string) => { const f = fields.find((x) => x.key === k); return f ? effective(k, f.value) : ''; };
    return draft.draftType === 'client'
      ? { mode: 'client-mono', form: { name: get('name'), commercialName: get('commercialName'), siren: get('siren') || (draft.siren ?? ''), siretHq: get('siret') || (draft.siret ?? ''), naf: get('naf'), legalForm: get('legalForm'), street: get('street') || get('voie'), cp: get('cp'), city: get('city') || get('ville'), contactEmail: get('email'), businessKind: get('businessKind') } }
      // ⚠️ LE CALENDRIER EST REPORTÉ, et ce n’est pas une commodité : sans ce report, le client répond
      //    à la question dans le formulaire, l’opérateur la revoit dans le dossier, et le wizard
      //    repart sur le DÉFAUT — la réponse du client est perdue en silence, au dernier mètre.
      : { mode: 'store', form: { stName: get('name'), stSiret: get('siret') || (draft.siret ?? ''), stStreet: get('street') || get('voie'), stCp: get('cp'), stCity: get('city') || get('ville'), ...calendrierDepose(draft.identity) } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, taken, draft]);

  const checks = [
    { ok: fields.length > 0, label: 'Identité déposée', note: fields.length ? `${fields.length} champ(s) renseigné(s)` : 'dossier vide' },
    { ok: !!draft.siren && /^\d{9}$/.test(draft.siren), label: 'SIREN', note: draft.siren ? (/^\d{9}$/.test(draft.siren) ? draft.siren : `« ${draft.siren} » — format invalide`) : 'non déposé' },
    { ok: !draft.siret || /^\d{14}$/.test(draft.siret), label: 'SIRET', note: draft.siret ? (/^\d{14}$/.test(draft.siret) ? draft.siret : 'format invalide') : 'non déposé (facultatif)' },
    { ok: !gap || Object.keys(gap).length === 0, label: 'Contrôle Sirene', note: gap && Object.keys(gap).length ? `${Object.keys(gap).length} écart(s) à arbitrer ci-contre` : 'aucun écart relevé au dépôt' },
  ];
  const OURS = ['GLN du magasin', 'N° magasin (S…) et n° de caisse', 'Radical EAN', 'Licences & régime de parc', 'Chaîne de connexion (DSN)'];

  return (
    <>
      {askingReason && (
        <div className="modalwrap" onClick={busy ? undefined : () => setAskingReason(false)}>
          <div className="modal" ref={reasonPanel} role="dialog" aria-modal="true" aria-labelledby={reasonTitle}
            style={{ width: 'min(94vw, 460px)' }} onClick={(e) => e.stopPropagation()}>
            <h3 id={reasonTitle}>Demander un complément</h3>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: -4, lineHeight: 1.5 }}>
              Le motif part dans l'e-mail et s'affiche en bannière quand le déposant rouvre son dossier —
              il doit dire précisément quoi corriger.
            </div>
            <textarea aria-label="Motif du complément demandé" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Ex. : le SIRET de l'établissement ne correspond pas à l'adresse déclarée."
              className="field-input" style={{ marginTop: 12, height: 'auto', padding: '9px 12px', resize: 'vertical', fontFamily: 'inherit' }} />
            <div className="modalfoot">
              <button className="ghost btn-ghost" onClick={() => setAskingReason(false)} disabled={busy}>Annuler</button>
              <button className="btn btn-cta sm" style={{ width: 'auto' }} disabled={busy || reason.trim().length < 5} onClick={() => void requestCorrection()}>
                {busy ? 'Envoi…' : 'Envoyer le lien de complément'}
              </button>
            </div>
          </div>
        </div>
      )}
      {wizardOpen && (
        <ProvisioningWizard prefill={prefill}
          onClose={() => setWizardOpen(false)}
          onDone={() => {
            setWizardOpen(false);
            // La création a EU LIEU (le wizard rapporte ses étapes réelles) → le brouillon passe validé.
            void api.updateDraft(draft.id, 'validated').then(() => { showToast('Brouillon validé — dossier créé via le wizard.'); onChanged(); })
              .catch((e) => void alert({ title: 'Dossier créé mais statut non posé', message: (e as Error).message }));
          }} />
      )}

      <button onClick={onBack} className="btn-link" style={{ fontSize: 'var(--t-body)', marginBottom: 12 }}>← Tous les brouillons</button>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <h1 className="page-title">{(draft.identity as { name?: string })?.name ?? 'Dossier sans nom'}</h1>
            <Pill bg="var(--vi-bg)" fg="var(--vi-fg)">{draft.draftType === 'client' ? 'nouveau client' : 'nouveau magasin'}</Pill>
            <Pill bg="var(--ne-bg)" fg="var(--ne-fg)">formulaire web</Pill>
            <Pill bg={st.bg} fg={st.fg}>{st.label}</Pill>
          </div>
          <div className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 5 }}>
            {draft.siren ? `SIREN ${draft.siren} · ` : ''}reçu le {dateTimeLong(draft.submittedAt)}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setAskingReason(true)} disabled={busy || draft.status !== 'deposited'}
            title={draft.status !== 'deposited' ? 'Seul un dossier « à relire » peut repartir en complément' : 'Renvoie un lien qui rouvre ce dossier pré-rempli chez le déposant'} className="btn-ghost" style={{ height: 36, padding: '0 15px', borderRadius: 'var(--r-sm)', fontSize: 'var(--t-body)', fontWeight: 700 }}>
            Demander un complément
          </button>
          <button onClick={() => setWizardOpen(true)} disabled={busy || draft.status === 'validated'}
            style={{ height: 36, padding: '0 15px', borderRadius: 'var(--r-sm)', border: 'none', background: draft.status === 'validated' ? 'var(--ink100)' : 'var(--cta)', color: draft.status === 'validated' ? 'var(--ink400)' : 'var(--cta-ink)', boxShadow: draft.status === 'validated' ? 'none' : 'var(--cta-sh)', fontSize: 'var(--t-body)', fontWeight: 700, cursor: draft.status === 'validated' ? 'default' : 'pointer' }}>
            {draft.status === 'validated' ? 'Déjà validé' : 'Valider — créer via le wizard'}
          </button>
        </div>
      </div>


      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 14, alignItems: 'start' }}>
        {/* Relecture champ par champ — saisie du client + écart Sirene serveur. */}
        <div className="dcard" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Relecture champ par champ</span>
            <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>saisie du client · contrôle Sirene fait au dépôt (serveur)</span>
          </div>
          {fields.length === 0 && <EmptyState icon="file" title="Dossier vide" note="Aucun champ n’a été déposé par le formulaire — rien à relire." />}
          {fields.map((fd) => {
            const ref = gap && typeof gap[fd.key] === 'string' ? String(gap[fd.key]) : null;
            const isTaken = taken[fd.key] !== undefined;
            // États maquette dérivables des données réelles : manquant (valeur vide) / écart / corrigé /
            // conforme — avec la TEINTE DE RANGÉE associée. « déclaratif » attend un schéma de champs typé.
            const missing = !isTaken && (fd.value == null || String(fd.value).trim() === '' || String(fd.value).trim() === '—');
            const state = missing
              ? { label: 'manquant', bg: 'var(--da-bg)', fg: 'var(--da-fg)', tint: 'color-mix(in srgb, var(--da-fg) 6%, transparent)' }
              : isTaken
                ? { label: 'corrigé sur Sirene', bg: 'var(--ok-bg)', fg: 'var(--ok-fg)', tint: 'color-mix(in srgb, var(--teal-500) 7%, transparent)' }
                : ref
                  ? { label: 'écart', bg: 'var(--wa-bg)', fg: 'var(--wa-fg)', tint: 'color-mix(in srgb, var(--amber) 6%, transparent)' }
                  : { label: 'conforme', bg: 'var(--ok-bg)', fg: 'var(--ok-fg)', tint: undefined };
            return (
              <div key={fd.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 18px', borderTop: '1px solid var(--ink100)', flexWrap: 'wrap', background: state.tint }}>
                <div style={{ minWidth: 150, flex: 1, fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>{fd.label}</div>
                <div style={{ minWidth: 170, flex: 1.3 }}>
                  <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, textDecoration: isTaken ? 'line-through' : undefined, color: isTaken ? 'var(--ink400)' : missing ? 'var(--ink400)' : 'var(--ink900)' }}>{missing ? '— non renseigné' : fd.value}</div>
                  {isTaken && <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ok-fg)' }}>{taken[fd.key]} <span style={{ fontWeight: 500, fontSize: 'var(--t-sub)' }}>(valeur Sirene retenue)</span></div>}
                  {ref && !isTaken && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                      <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--wa-fg)' }}>Sirene : {ref}</span>
                      <button onClick={() => setTaken((p) => ({ ...p, [fd.key]: ref }))} className="btn-ghost" style={{ height: 24, padding: '0 9px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-sub)', fontWeight: 600 }}>
                        Prendre cette valeur
                      </button>
                    </div>
                  )}
                </div>
                <Pill bg={state.bg} fg={state.fg}>{state.label}</Pill>
              </div>
            );
          })}
          <div style={{ padding: '11px 18px', borderTop: '1px solid var(--ink200)', background: 'var(--ink100)', fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5 }}>
            « Prendre cette valeur » n'écrase pas le dépôt (la saisie du client reste intacte) : la valeur retenue part dans le wizard à la validation.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="dcard" style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 11 }}>Contrôles</div>
            {checks.map((c) => (
              <div key={c.label} style={{ display: 'flex', gap: 10, padding: '7px 0' }}>
                <span className="dot" style={{ background: c.ok ? 'var(--chart-ok)' : 'var(--chart-wa)', marginTop: 4 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--t-dense)', fontWeight: 600 }}>{c.label}</div>
                  <div className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 2, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{c.note}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="dcard" style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 9 }}>Attribué par nous à la validation</div>
            {OURS.map((o) => (
              <div key={o} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)', flexShrink: 0 }}>🔒</span>
                <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink600)', lineHeight: 1.45 }}>{o}</span>
              </div>
            ))}
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 10, lineHeight: 1.5 }}>
              Ces valeurs ne figurent pas dans le formulaire : le client ne peut ni les proposer, ni les falsifier.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
